/**
 * How the shop's goods pack onto oven trays.
 *
 * Three rules, given by the shop, and all three turn out to be the same shape
 * once a tray is counted in slots rather than in pieces:
 *
 *   A tray takes four focaccia, and focaccia only.
 *
 *   A tray takes four breads, and bread only — except that Cranberry Walnut
 *   and Raisin Cinnamon are big, so a tray carrying one holds three. Counting
 *   an ordinary loaf as three slots and a big one as four, out of twelve,
 *   gives exactly that: four ordinary, or three big, or any mix of three where
 *   one of them is big, and never four with a big among them.
 *
 *   A tray takes twelve full-size croissants or twenty-four minis, and the two
 *   may share — "an order of six and an order of twelve minis bake together".
 *   Twenty-four slots, two to a full-size and one to a mini, is that rule.
 *
 * Keeping it in slots means one packer serves all three, and a change to any
 * rule is a change to one number here rather than to the code that uses it.
 */
const TRAYS = {
    focaccia: { capacity: 4, label: 'Focaccia', rule: '4 a tray' },
    bread: { capacity: 12, label: 'Bread', rule: '4 a tray, 3 if a big one is on it' },
    pastry: { capacity: 24, label: 'Pastries', rule: '12 full-size or 24 mini a tray' },
};
export function trayRule(trayClass) {
    return TRAYS[trayClass];
}
/**
 * Which goods are worth laying onto trays on the page, and which are a total.
 *
 * Only the pastries. Bread and focaccia go four to a tray, the baker has known
 * that for years, and printing which four told them their job while splitting
 * five rosemary across two trays made a plain number hard to read.
 *
 * Pastries are different because the arithmetic is not obvious: a full-size
 * croissant and a mini take different space, they may share a tray, and
 * "twelve or twenty-four" depends on what else is on it. That is worth working
 * out on paper rather than at the bench.
 */
const PLANNED = ['pastry'];
export function needsTrayPlan(trayClass) {
    return PLANNED.includes(trayClass);
}
/**
 * Goods that never see the oven and are still the kitchen's work.
 *
 * The infused oil is made here — steeped and bottled — so the kitchen has to
 * know how many are wanted. Everything else that is not baked is retail or
 * packing: a dipping set is assembled, a spread, a bottle of plain oil or
 * balsamic and a gift card are taken off a shelf, and a latte is pulled at the
 * counter. All of that is done from the receipts by whoever packs the order.
 *
 * Written as what to *include* rather than what to leave out, so that adding a
 * new jar to the catalogue tomorrow does not quietly put it on the kitchen's
 * page. Matching "infused" rather than the full product name on purpose: the
 * plain `Olive Oil, 250ml` must not be caught, and `Infused olive oil, 250ml`
 * must be, and the two differ only by that word.
 */
const PREPARED_WITHOUT_BAKING = [/\binfused\b/i];
export function isKitchenPrepared(name) {
    return PREPARED_WITHOUT_BAKING.some((pattern) => pattern.test(name));
}
/**
 * The two breads that take a tray of three.
 *
 * Matched on the name because nothing in the catalogue marks a loaf as big —
 * there is no size field, and the recipes that would have implied it are not
 * filled in. Written loosely enough to survive a capitalisation or a stray
 * word, and narrow enough not to catch a future "Cranberry Focaccia".
 */
const BIG_BREADS = [/cranberry\s*walnut/i, /raisin\s*cinnamon/i];
/** A mini pastry: a dozen to a box and half the tray space of a full one. */
const MINI = /^\s*mini\b/i;
/** "Classic croissants, box of 6" — the box holds six, and six get baked. */
const BOX_OF = /,?\s*box of\s*(\d+)\s*$/i;
/** ", pcs" is how the catalogue marks the single-piece line. */
const PIECE_SUFFIX = /,?\s*pcs?\.?\s*$/i;
/**
 * Read one catalogue line as the oven sees it.
 *
 * The category decides the tray, because it is the only field that reliably
 * separates a focaccia from a loaf from a croissant — `has_recipe` would be
 * the natural choice and is false for all but two products in the catalogue,
 * so a sheet built on it would send the kitchen almost nothing.
 *
 * Anything outside the three baking categories comes back with no tray class:
 * oil, gift cards, a tote bag. The sheet lists those separately rather than
 * dropping them, because a good the kitchen never sees is a good nobody packs.
 */
export function planGood(good) {
    const boxed = BOX_OF.exec(good.name);
    const piecesPerUnit = boxed ? Number(boxed[1]) : 1;
    const name = good.name.replace(BOX_OF, '').replace(PIECE_SUFFIX, '').trim();
    const category = good.categoryName.toLowerCase();
    const mini = MINI.test(name);
    if (category.includes('focaccia')) {
        return { productId: good.productId, name, trayClass: 'focaccia', slots: 1, piecesPerUnit, size: null, isBox: Boolean(boxed) };
    }
    if (category.includes('bread') || category.includes('loaf')) {
        const big = BIG_BREADS.some((pattern) => pattern.test(name));
        return {
            productId: good.productId,
            name,
            trayClass: 'bread',
            slots: big ? 4 : 3,
            piecesPerUnit,
            size: big ? 'big' : null,
            isBox: Boolean(boxed),
        };
    }
    if (category.includes('pastr') || category.includes('box')) {
        return {
            productId: good.productId,
            name,
            trayClass: 'pastry',
            slots: mini ? 1 : 2,
            piecesPerUnit,
            size: mini ? 'mini' : null,
            isBox: Boolean(boxed),
        };
    }
    return { productId: good.productId, name, trayClass: null, slots: 0, piecesPerUnit, size: null, isBox: Boolean(boxed) };
}
/**
 * Lay the day's baking onto trays.
 *
 * Largest pieces first, then alphabetically, then into the first tray with
 * room. Taking the awkward ones first is what stops a half-empty tray of big
 * breads appearing at the end, and placing the same good consecutively keeps
 * it together on a tray without having to ask for that separately.
 *
 * Trays are numbered across the whole sheet rather than within a class, so
 * "tray 7" means one tray on one bench and not three different ones.
 */
export function packTrays(pieces, startIndex = 1) {
    const order = ['focaccia', 'bread', 'pastry'];
    const trays = [];
    let index = startIndex;
    for (const trayClass of order) {
        const forClass = pieces.filter((piece) => piece.trayClass === trayClass && piece.pieces > 0);
        if (forClass.length === 0)
            continue;
        const queue = forClass
            .slice()
            .sort((a, b) => b.slots - a.slots || a.name.localeCompare(b.name) || (a.note ?? '').localeCompare(b.note ?? ''))
            .flatMap((piece) => Array.from({ length: piece.pieces }, () => piece));
        const { capacity } = TRAYS[trayClass];
        const open = [];
        for (const piece of queue) {
            let slot = open.find((candidate) => candidate.tray.used + piece.slots <= capacity);
            if (!slot) {
                const tray = { index: index++, trayClass, lines: [], used: 0, capacity, isPartial: true };
                slot = { tray, lines: new Map() };
                open.push(slot);
                trays.push(tray);
            }
            slot.tray.used += piece.slots;
            const key = `${piece.productId} ${piece.note ?? ''}`;
            const line = slot.lines.get(key);
            if (line)
                line.qty += 1;
            else
                slot.lines.set(key, {
                    productId: piece.productId,
                    name: piece.name,
                    qty: 1,
                    size: piece.size,
                    note: piece.note ?? null,
                });
        }
        for (const { tray, lines } of open) {
            // Biggest first inside a tray too, so a big bread leads the line it explains.
            tray.lines = [...lines.values()].sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name));
            tray.isPartial = tray.used < capacity;
        }
    }
    return trays;
}
//# sourceMappingURL=kitchen.js.map